import controlPlaneApi from '../config/controlPlaneApi'
import { CONTROL_PLANE_OWNER_KEY, CONTROL_PLANE_TOKEN_KEY } from '../config/controlPlaneApi'
import { OwnerRegistration } from '../types/onboardingRegistration'
import { LicensePlan } from '../types/license'
import { ControlPlaneSession } from '../types/controlPlane'

export interface RegisterOwnerResponse {
  message: string
  email: string
}

export interface VerifyEmailResponse extends ControlPlaneSession {
  organization: {
    organizationId: string
    name: string
    slug: string
  }
}

export interface TrackInterestResponse {
  meetingUrl: string
  hubspotConfigured: boolean
  hubspotContactId: string | null
}

export const registerOwnerApi = async (payload: OwnerRegistration): Promise<RegisterOwnerResponse> => {
  const response = await controlPlaneApi.post<RegisterOwnerResponse>('/control-plane/auth/register', payload)
  return response.data
}

/** Share one in-flight verify call per token (React Strict Mode remounts). */
const verifyEmailInFlight = new Map<string, Promise<VerifyEmailResponse>>()

export const verifyEmailApi = async (token: string): Promise<VerifyEmailResponse> => {
  const existing = verifyEmailInFlight.get(token)
  if (existing) return existing

  const promise = controlPlaneApi
    .post<VerifyEmailResponse>('/control-plane/auth/verify-email', { token })
    .then((response) => response.data)
    .catch(async (err) => {
      const message = String(
        err?.response?.data?.message || err?.message || ''
      )
      // Race leftover: org/user already created — retry once (idempotent path).
      if (/already exists|existe déjà|User exists|Conflict/i.test(message)) {
        await new Promise((r) => setTimeout(r, 500))
        const retry = await controlPlaneApi.post<VerifyEmailResponse>(
          '/control-plane/auth/verify-email',
          { token }
        )
        return retry.data
      }
      throw err
    })
    .finally(() => {
      verifyEmailInFlight.delete(token)
    })

  verifyEmailInFlight.set(token, promise)
  return promise
}

export const resendVerificationApi = async (email: string): Promise<{ message: string }> => {
  const response = await controlPlaneApi.post<{ message: string }>('/control-plane/auth/resend-verification', {
    email,
  })
  return response.data
}

export const trackCrmInterestApi = async (input: {
  email: string
  source: 'request-demo' | 'plan-select'
  plan?: LicensePlan
  firstName?: string
  lastName?: string
  phone?: string
  jobTitle?: string
  companyName?: string
  companyAddress?: string
  country?: string
  need?: string
  username?: string
}): Promise<TrackInterestResponse> => {
  const response = await controlPlaneApi.post<TrackInterestResponse>(
    '/control-plane/crm/track-interest',
    input
  )
  return response.data
}

export function persistControlPlaneSession(session: ControlPlaneSession): void {
  localStorage.setItem(CONTROL_PLANE_TOKEN_KEY, session.token)
  localStorage.setItem(CONTROL_PLANE_OWNER_KEY, JSON.stringify(session.owner))
}

export function loadControlPlaneSession(): ControlPlaneSession | null {
  const token = localStorage.getItem(CONTROL_PLANE_TOKEN_KEY)
  const ownerRaw = localStorage.getItem(CONTROL_PLANE_OWNER_KEY)
  if (!token || !ownerRaw) return null
  try {
    return { token, owner: JSON.parse(ownerRaw) }
  } catch {
    return null
  }
}
